import { useEffect } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, CheckCircle2 } from "lucide-react";
import { KineticLines, Reveal, Eyebrow } from "@/components/site/Reveal";
import { LeadDrawer } from "@/components/site/LeadDrawer";
import { SOLUTIONS } from "@/data/content";
import { SOLUTION_EXTRAS, LEAD_TIERS } from "@/data/solutionPages";

export default function SolutionDetail() {
  const { slug } = useParams();
  const solution = SOLUTIONS.find((s) => s.id === slug);

  useEffect(() => {
    if (solution) document.title = `${solution.title} | Vyacomm Media`;
  }, [solution]);

  if (!solution) return <Navigate to="/solutions" replace />;

  const extra = SOLUTION_EXTRAS[slug] || { stats: [] };
  const headlineLines = solution.headline.split(/(?<=\.)\s+/);
  const Icon = solution.icon;

  return (
    <div data-testid={`solution-page-${slug}`}>
      <section className="grain hero-grid-lines relative overflow-hidden bg-navy pb-24 pt-[200px]">
        <div className="pointer-events-none absolute -right-40 top-10 h-[420px] w-[420px] rounded-full bg-cobalt/20 blur-[140px]" />
        <div className="relative z-10 mx-auto max-w-[1808px] px-5 sm:px-8 lg:px-12 2xl:px-14">
          <Reveal delay={0.05} y={14}>
            <Link
              to="/solutions"
              data-testid="solution-back-link"
              className="inline-flex items-center gap-2 font-mono2 text-[11px] tracking-[0.2em] text-slate-400 transition-colors hover:text-white"
            >
              <ArrowLeft className="h-3.5 w-3.5" /> ALL SOLUTIONS
            </Link>
          </Reveal>
          <div className="mt-8 flex items-center gap-4">
            <span className="grid h-14 w-14 place-items-center rounded-2xl bg-cobalt/15 text-cobaltlight">
              <Icon className="h-7 w-7" />
            </span>
            <Eyebrow dark>{solution.tag}</Eyebrow>
          </div>
          <h1 className="mt-6 max-w-4xl font-jakarta text-4xl font-extrabold leading-[1.08] tracking-tight text-white sm:text-5xl lg:text-6xl">
            <KineticLines lines={headlineLines} accentIndex={headlineLines.length - 1} />
          </h1>
          <Reveal delay={0.5}>
            <p className="mt-6 max-w-2xl text-base leading-relaxed text-slate-300 sm:text-lg">{solution.desc}</p>
          </Reveal>
          <Reveal delay={0.65}>
            <div className="mt-9 flex flex-wrap items-center gap-4">
              <Link
                to="/contact-us"
                data-testid="solution-hero-cta"
                className="group inline-flex items-center gap-2 rounded-full bg-cobalt px-7 py-4 text-sm font-bold text-white shadow-[0_18px_44px_-12px_rgba(37,99,235,0.8)] transition-all duration-300 hover:scale-[1.03] hover:bg-[#1d4fd7] active:scale-95"
              >
                Scope This Solution
                <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
              <LeadDrawer
                buttonClass="inline-flex items-center gap-2 rounded-full border border-white/20 px-7 py-4 text-sm font-semibold text-white transition-all duration-300 hover:border-cobaltlight hover:bg-white/5"
              />
            </div>
          </Reveal>
        </div>
      </section>

      <section className="border-b border-line bg-white" data-testid="solution-stats">
        <div className="mx-auto grid max-w-[1808px] sm:grid-cols-3">
          {extra.stats.map(([v, l], i) => (
            <Reveal key={l} delay={i * 0.08} className={`border-line px-8 py-10 ${i < 2 ? "sm:border-r" : ""} border-b sm:border-b-0`}>
              <p className="font-jakarta text-4xl font-extrabold tracking-tight text-navy">{v}</p>
              <p className="mt-2 text-xs leading-snug text-slate-500">{l}</p>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="bg-canvas py-24 sm:py-32" data-testid="solution-deliverables">
        <div className="mx-auto max-w-[1808px] px-5 sm:px-8 lg:px-12 2xl:px-14">
          <Reveal>
            <Eyebrow>What You Receive</Eyebrow>
            <h2 className="mt-4 max-w-2xl font-jakarta text-3xl font-extrabold tracking-tight text-navy sm:text-4xl">
              Every engagement, engineered to spec
            </h2>
          </Reveal>
          <div className="mt-12 grid gap-4 sm:grid-cols-2">
            {solution.bullets.map((b, i) => (
              <Reveal key={b.t} delay={i * 0.07}>
                <div className="h-full rounded-2xl border border-line bg-white p-7 transition-all duration-300 hover:-translate-y-1 hover:border-cobalt/50 hover:shadow-[0_20px_50px_-24px_rgba(11,25,44,0.25)]">
                  <CheckCircle2 className="h-5 w-5 text-cobalt" />
                  <h3 className="mt-3 font-jakarta text-lg font-bold text-navy">{b.t}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-slate-600">{b.d}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.15}>
            <div className="mt-8 flex flex-wrap gap-2">
              {solution.specs.map((sp) => (
                <span key={sp} className="rounded-full border border-line bg-white px-4 py-2 font-mono2 text-[10px] tracking-[0.14em] text-slate-500">
                  {sp.toUpperCase()}
                </span>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {slug === "qualified-leads" && (
        <section className="bg-white py-24 sm:py-32" data-testid="lead-tiers-table">
          <div className="mx-auto max-w-[1808px] px-5 sm:px-8 lg:px-12 2xl:px-14">
            <Reveal>
              <Eyebrow>Lead Tier Matrix</Eyebrow>
              <h2 className="mt-4 max-w-2xl font-jakarta text-3xl font-extrabold tracking-tight text-navy sm:text-4xl">
                Four tiers of qualification rigor
              </h2>
            </Reveal>
            <Reveal delay={0.12} className="mt-12">
              <div className="overflow-hidden rounded-2xl border border-line">
                <div className="hidden grid-cols-12 gap-6 bg-navy px-6 py-4 sm:grid">
                  <span className="col-span-3 font-mono2 text-[10px] tracking-[0.22em] text-slate-400">LEAD LEVEL</span>
                  <span className="col-span-5 font-mono2 text-[10px] tracking-[0.22em] text-slate-400">QUALIFICATION CRITERIA</span>
                  <span className="col-span-4 font-mono2 text-[10px] tracking-[0.22em] text-flame">IDEAL COMMERCIAL USE CASE</span>
                </div>
                {LEAD_TIERS.map((t, i) => (
                  <div key={t.level} className={`grid gap-4 px-6 py-6 sm:grid-cols-12 sm:gap-6 ${i % 2 === 0 ? "bg-canvas" : "bg-white"} border-b border-line last:border-b-0`}>
                    <h3 className="col-span-3 font-jakarta text-sm font-bold text-navy">{t.level}</h3>
                    <p className="col-span-5 text-sm leading-relaxed text-slate-600">{t.criteria}</p>
                    <p className="col-span-4 text-sm leading-relaxed text-charcoal">{t.use}</p>
                  </div>
                ))}
              </div>
            </Reveal>
          </div>
        </section>
      )}

      <section className="grain relative overflow-hidden bg-navy py-20" data-testid="solution-cta-banner">
        <div className="relative z-10 mx-auto flex max-w-[1808px] flex-col items-start justify-between gap-6 px-5 sm:flex-row sm:items-center sm:px-8 lg:px-12 2xl:px-14">
          <div>
            <h2 className="max-w-xl font-jakarta text-2xl font-extrabold text-white sm:text-3xl">
              Get a feasibility count for {solution.title.toLowerCase()} within 24 hours.
            </h2>
            <p className="mt-2 font-mono2 text-[10px] tracking-[0.2em] text-slate-400">
              ZERO OBLIGATION · CUSTOM CPL PRICING · 100% OPT-IN VERIFIED
            </p>
          </div>
          <Link
            to="/contact-us"
            data-testid="solution-bottom-cta"
            className="group inline-flex shrink-0 items-center gap-2 rounded-full bg-flame px-7 py-4 text-sm font-bold text-navy transition-all duration-300 hover:scale-[1.03] hover:bg-[#ffb43a]"
          >
            Request Custom Proposal
            <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
          </Link>
        </div>
      </section>
    </div>
  );
}
